import React from 'react';
import TextInput from './TextInput';
import './textInput.scss';

/** @import { ValidatedInputProps, ValidatedInputState, ValidationError } from "./ValidatedInputTypes" */

/**
 * @template {ValidatedInputProps} [TProps=ValidatedInputProps]
 * @extends {TextInput<TProps>} 
 ***/
class ValidatedInput extends TextInput {
    /** @type {ValidatedInputState} */
    state = { value: '' };

    constructor(/** @type {TProps} */ props) {
        super(props);

        let results = this.testAllRules(this.props.value || '');
        this.state = {
            value: this.props.value,
            ...results.validationResults,
            allValid: results.allValid,
            hasValidationErrors: this.hasValidationErrors(this.props.validationErrors, results.validationResults),
            touched: false
        };
    }

    handleOnChange = (/** @type {React.ChangeEvent<HTMLInputElement>} */ e) => {
        const { value } = e.target;

        // call to superclass' change handler, without it firing onChange handler
        this.handleChange(e, true);

        let results = this.testAllRules(value);
        this.setState({
            ...results.validationResults,
            hasValidationErrors: this.hasValidationErrors(this.props.validationErrors, results.validationResults)
        }, () => {
            // this is necessary to evaluate the latest props.validationErrors from the onChange above
            let reevaluatedResults = this.testAllRules(value);

            // trigger change handle with validation results
            this.props.onValidation && this.props.onValidation(reevaluatedResults.allValid);
        });
    };

    handleOnBlur = (/** @type {React.FocusEvent<HTMLInputElement, Element>} */ e) => {
        if (this.props.onBlur) {
            this.props.onBlur(e);
        }

        const { value } = e.target;

        let results = this.testAllRules(value);
        this.setState({
            allValid: results.allValid,
            ...results.validationResults,
            hasValidationErrors: this.hasValidationErrors(this.props.validationErrors, results.validationResults),
            touched: true
        }, () => {
            // this is necessary to evaluate the latest props.validationErrors from the onChange above
            let reevaluatedResults = this.testAllRules(value);

            // trigger change handle with validation results
            this.props.onValidation && this.props.onValidation(reevaluatedResults.allValid);
        });
    };

    testAllRules(/** @type {string} */ value) {
        const { rules, validationErrors } = this.props;

        // calculate value validity based on all rules:
        /** @type {Record<string, boolean>} */ 
        const validationResults = rules 
            ? Object.keys(rules)
                .reduce((accumulator, ruleName) => ({
                    ...accumulator,
                    [ruleName]: rules[ruleName].test(value)
                }), {})
            : {};

        const rulesPass = Array.from(Object.values(validationResults)).every(validationResult => validationResult);

        let noServerValidationErrors = !validationErrors || validationErrors.length === 0;
        let allValid = rulesPass && noServerValidationErrors;

        return {
            allValid: allValid,
            validationResults: validationResults
        }
    }

    hasValidationErrors(/** @type {ValidationError[] | undefined} */ validationErrors, /** @type {Record<string, boolean>} */ validationResults) {
        return (validationErrors && validationErrors.length > 0) || (validationResults && Object.values(validationResults).includes(false));
    }

    getContainerClassNames() {
        return [
            'validated-input',
            this.props.onlyErrors ? 'validated-input--only-errors' : ''
        ].join(' ').trim();
    }

    getInputClassNames() {
        return [
            this.getClassNames(),
            ((this.props.rules && Object.keys(this.props.rules).length > 0) || (this.props.validationErrors && this.props.validationErrors.length > 0)) && this.props.showValidationErrors !== false ? 'validated-input__input' : '',
            this.state.touched && this.state.hasValidationErrors ? 'error' : ''
        ].join(' ').trim();
    }

    getRuleClassNames(/** @type {string} */ ruleName) {
        return [
            'validation-rule',
            this.props.altErrorColor ? 'validation-rule--alt-errors' : '',
            this.state[ruleName] ? 'validation-rule--pass' : ''
        ].join(' ').trim();
    }

    renderValidations() {
        if (this.props.showValidationErrors === false) return;

        const { rules, validationErrors } = this.props;
        const { value } = this.state;

        let { allValid } = this.testAllRules(value || '');
        let notEmpty = value !== '';
        let invalid = allValid === false;
        let showRules = (notEmpty && invalid) || this.props.alwaysShowRules;

        return (rules || validationErrors) && (
            <ul className='validated-input__validations'>
                <>
                    {showRules && rules && Object.keys(rules).map((ruleName) => (
                        !!rules[ruleName].label && (
                            <li key={ruleName} className={this.getRuleClassNames(ruleName)}>
                                <span className={'validated'}></span>
                                <span className={'validation-rule__label'}>{rules[ruleName].label}</span>
                            </li>)
                    ))}
                    {validationErrors && validationErrors.map((error, i) => (
                        <li key={i} className="validation-rule validation-rule--alt-errors">
                            <div dangerouslySetInnerHTML={{ __html: error.message }} />
                        </li>
                    ))}
                </>
            </ul>
        );
    }

    render() {
        return (
            <div className={this.getContainerClassNames()}>
                <div className="text-block text-block-loading">
                    {this.props.label && (<label htmlFor={this.props.name}>{this.props.label}</label>)}
                    <input
                        name={this.props.name}
                        className={this.getInputClassNames()}
                        type='text'
                        placeholder={this.props.placeholder}
                        onChange={this.handleOnChange}
                        onBlur={this.handleOnBlur}
                        disabled={this.props.disabled}
                        ref={this.props.inputRef}
                        value={this.props.value}
                        maxLength={this.props.maxLength}
                        data-test={this.props.name}
                    />
                    {this.props.showLoading && (
                        <div className="lds-facebook">
                            <div />
                            <div />
                            <div />
                            <div />
                            <div />
                            <div />
                            <div />
                            <div />
                            <div />
                            <div />
                            <div />
                            <div />
                        </div>
                    )}
                </div>
                {this.renderValidations()}
            </div>
        );
    }
}

export default ValidatedInput;
